import {useCallback, useEffect} from 'react';

/**
 * Returns a coupled pair of hooks that can be used to emit and subscribe to arbitrary events
 *
 * @example
 * const {useEmit, useListener} = createSubscribableEvent();
 *
 * const EmitterComponent = () => {
 *  const emit = useEmit();
 *
 *  return <button onClick={emit}>Emit</button>;
 * };
 *
 * const ListenerComponent = () => {
 *  useListener(() => {
 *      // react to the event (it carries no payload)
 *  });
 *
 *  return null;
 * };
 */
export const createSubscribableEvent = () => {
    const listeners = new Set<() => void>();

    const useEmit = () => {
        const emit = useCallback(() => {
            listeners.forEach(sub => sub());
        }, []);

        return emit;
    };

    const useListener = (listener: () => void) => {
        useEffect(() => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        }, [listener]);
    };

    return {useEmit, useListener};
};
